"use client";

import { useEffect, useRef } from "react";
import { useCartStore } from "@/lib/store/cartStore";
import { authClient } from "@/lib/auth-client";

const MIGRATION_FLAG = "cart-session-migrated-v1";

/**
 * CartMigration Component
 * 
 * Automatically fixes the cart sessionId issue for authenticated users.
 * This runs once per user and:
 * 1. Detects if user is authenticated but has a sessionId in localStorage
 * 2. Merges any guest cart items to the authenticated user
 * 3. Clears the sessionId from localStorage
 * 4. Sets a migration flag so this only runs once
 * 
 * This fixes the production issue where users can't see their cart items
 * because the queries are looking for sessionId instead of userId.
 */
export function CartMigration() {
  const { sessionId, mergeGuestCart, clearSessionForAuthenticatedUser } = useCartStore();
  const migrationAttempted = useRef(false);

  useEffect(() => {
    // Only run once per mount
    if (migrationAttempted.current) return;
    migrationAttempted.current = true;

    const runMigration = async () => {
      try {
        // Check if migration already completed
        const migrationCompleted = localStorage.getItem(MIGRATION_FLAG);
        if (migrationCompleted === "true") {
          console.log("[CartMigration] Migration already completed, skipping");
          return;
        }

        // Check if user is authenticated
        const session = await authClient.getSession();
        const isAuthenticated = !!session?.data?.user;

        if (!isAuthenticated) {
          console.log("[CartMigration] User not authenticated, skipping migration");
          return;
        }

        // Check if sessionId exists (the problem state)
        if (!sessionId) {
          console.log("[CartMigration] No sessionId found, marking migration as complete");
          localStorage.setItem(MIGRATION_FLAG, "true");
          return;
        }

        console.log("[CartMigration] Detected authenticated user with sessionId, running migration");
        console.log("[CartMigration] SessionId:", sessionId);

        // Merge guest cart items to authenticated user
        try {
          await mergeGuestCart();
          console.log("[CartMigration] Successfully merged guest cart");
        } catch (error) {
          console.error("[CartMigration] Failed to merge guest cart:", error);
          // Continue with clearing sessionId even if merge fails
        }

        // Clear sessionId from store
        clearSessionForAuthenticatedUser();
        console.log("[CartMigration] Cleared sessionId from store");

        // Mark migration as complete
        localStorage.setItem(MIGRATION_FLAG, "true");
        console.log("[CartMigration] Migration completed successfully");

        // Force a small delay to ensure state updates propagate
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        console.error("[CartMigration] Migration failed:", error);
        // Don't set migration flag on failure so it can retry next time
      }
    };

    runMigration();
  }, [sessionId, mergeGuestCart, clearSessionForAuthenticatedUser]);

  return null;
}
